import type { AccessGroup } from '@/api/auth';
import type { Dashboard } from '@/api/dashboard';
import { CANDIDATES_PATH } from '@/pages/Candidates/candidateFilters';
import { QUESTIONS_PATH } from '@/pages/QuestionBank/questionBankUrls';
import { VACANCIES_PATH } from '@/pages/Vacancies/vacancyUrls';

export type SetupState = Dashboard['setup'];

export interface SetupStep {
  key: string;
  /** The screen that performs the step. */
  to: string;
  /**
   * The groups that may **perform** this step — never the groups that may open the screen it
   * points at, and the two differ. §6.8 gives Vacancies to *"Admin (edit), Recruiter (read)"*, so
   * `navItems.ts` lets a Recruiter reach `/vacancies`; but creating the first vacancy is Admin's,
   * so the vacancy step below is `['Admin']` and a Recruiter is never offered it.
   *
   * ⚠️ The field is deliberately **not** called `groups`, which is what `NavItem` calls its own
   * (visibility) list. Under one name the two read as the same thing, and the next edit reconciles
   * them: adding `'Recruiter'` here to match `navItems.ts` would offer a Recruiter a button to
   * create something §6.8 only lets them read.
   */
  performedBy: AccessGroup[];
  done: (setup: SetupState) => boolean;
}

/**
 * §6.2's three setup steps, in the design's order, each with the screen that performs it.
 *
 * ⚠️ **All three destinations are real screens, and the paragraph that stood here is retracted rather
 * than left to read as current** (TASK-077, 2026-07-29). It stated that not one of the three was built,
 * that each answered with §5's placeholder, and that the path would already point at each screen when
 * it arrived. Each arrived — `/candidates` with TASK-062, `/vacancies` with TASK-071 and `/questions`
 * with TASK-077 — so a reader of that paragraph would now be told the path leads to three
 * placeholders, which is the failure `CLAUDE.md` names: never restate a retracted claim. Its last
 * clause is the part that held, and it is why the destination arriving cost this table nothing but its
 * own docblock. **This module no longer says anything about a screen not existing**, and
 * `setupPath.test.tsx` keeps it that way from both ends: it walks each `to` through
 * `matchRoutes(buildRoutes(), …)` **and** asserts the answer is the screen itself rather than §5's
 * placeholder, so the fact can no longer drift away from the prose.
 *
 * ⚠️ **Each `to` is the address's own constant, not a literal here** (TASK-077, discharging the half of
 * TASK-071's criterion that was this module's). One place spells `/vacancies`, one spells `/candidates`
 * and one spells `/questions`, and none of them is this file.
 *
 * `performedBy` is who may **do** the step, not who may look at the screen: creating a vacancy is an
 * Admin's (§6.8 *"Admin (edit)"*), adding a candidate is a Recruiter's or an Admin's (§6.4). A
 * Recruiter may open `/vacancies` and read it, which is why `navItems.ts` lists them there and this
 * table does not.
 *
 * ⚠️ **Writing a question stopped being an Admin's, and step 2's `performedBy` deliberately did not
 * move** (2026-08-11, ADR 0018, TASK-135). It reads `['Admin']` because the whole bank surface once
 * answered a Recruiter and an Interviewer 403; `POST …/question-sets/{id}/questions/` admits **any of the three
 * groups** (ADR 0018) — so this entry is no
 * longer a statement about permission. What it still decides is which step §6.2 offers a group as
 * **their next action** on a first-run tenant, and widening it would send a Recruiter to fill a set
 * before adding a candidate. That is §6.2's guided path rather than ADR 0018's, so it is left
 * as it is and named here rather than changed inside a task whose scope is §6.9.
 *
 * ⚠️ **The disclosure paragraph that stood here is retracted with it.** It recorded that a Recruiter
 * reads *"2 Add questions to it"* and thereby learns a screen exists that §5 refuses them, and that
 * *"`/questions` still answers `RequireGroup` with §8's refusal if they type it in"*. Both halves are
 * false now: §5 gives every group the *Questions* item and the address carries no group gate at all
 * (`routes.tsx`). Nothing about the **rendered list** changed — `SetupPath` still renders all three
 * steps whole, which is §6.2's own decision about a numbered path — and the trade it was recording
 * simply no longer exists for this step. `canWalkSetupPath`, which withholds the whole path from an
 * Interviewer, is untouched: §6.2 is the authority for that and this task did not reopen it.
 *
 * It stays whole because §6.2 fixes the path as three numbered steps and a legible path is the
 * point: filtering per group would renumber a design-specified list — a Recruiter would read "1 Add
 * a candidate" for the third step — which is design decided in a fix pass. **§6.2 is the authority
 * for keeping it, not an oversight.** If that trade is to be reopened, it is reopened in the design,
 * with `canWalkSetupPath` (which already withholds the whole path from an Interviewer) as the
 * precedent for what a per-group answer looks like.
 *
 * This table lives beside `SetupPath` rather than inside it because a module that exports both a
 * component and a plain function breaks Fast Refresh (`react-refresh/only-export-components`), and
 * the CI lint job runs with `--max-warnings 0`.
 */
export const SETUP_STEPS: SetupStep[] = [
  {
    key: 'vacancy',
    to: VACANCIES_PATH,
    performedBy: ['Admin'],
    done: (setup) => setup.has_vacancy,
  },
  /**
   * §6.9 itself. ⚠️ **The reason recorded here was that `/questions` is the *entry point* to a bank
   * rather than a bank — a chooser, because `has_questions` is a tenant-wide boolean and
   * `DashboardOut` carries no vacancy id to build `/vacancies/:vacancyId/questions` from.** Since ADR
   * 0018 there is no id to want, and since **ADR 0019** (2026-09-24) the *tenant-wide library* half of
   * that reason is **retracted** too: `/questions` lists the tenant's **question sets**, and a
   * question is written inside one.
   * ~~this address is the library~~ is corrected rather than the destination changed — the address is
   * the same one §6.2 has pointed at since TASK-077, and *"Add questions to it"* is still what a reader
   * does there, with one step in front of it (make a set) that the screen's own empty state offers.
   * `has_questions` is untouched and is still the tenant-wide boolean this step reads.
   */
  {
    key: 'questions',
    to: QUESTIONS_PATH,
    performedBy: ['Admin'],
    done: (setup) => setup.has_questions,
  },
  {
    key: 'candidate',
    to: CANDIDATES_PATH,
    performedBy: ['Admin', 'Recruiter'],
    done: (setup) => setup.has_candidate,
  },
];

/**
 * The step after the three, for the tenant that has done all of them and still has no submitted
 * interview — the case that made this path necessary in the first place. §8 asks an empty state for
 * one action, and "nothing left to set up" is not one, so it points at the candidate whose
 * interview is waiting to be run.
 */
export const INTERVIEW_STEP: SetupStep = {
  key: 'interview',
  to: CANDIDATES_PATH,
  performedBy: ['Admin', 'Recruiter'],
  done: () => false,
};

/**
 * Whether this viewer can act on the setup path at all.
 *
 * ⚠️ **A path nobody in this group may walk is not an empty state, it is an instruction addressed
 * to somebody else.** Found in a live browser: an Interviewer on a tenant with no vacancy was told
 * *"Set up the panel · Add a vacancy"* — with no Vacancies item in their navigation and no
 * permission to create one. An Interviewer sees §6.2 Section 1 instead, which on such a tenant is
 * its own quiet line ("No feedback is outstanding.") and never a grid of zeros, because an
 * Interviewer's payload carries no other section (ADR 0015).
 */
export const canWalkSetupPath = (groups: AccessGroup[]): boolean =>
  [...SETUP_STEPS, INTERVIEW_STEP].some((step) =>
    step.performedBy.some((group) => groups.includes(group)),
  );
